// ─── Angular Core ────────────────────────────────
import { ChequeAlertService } from '@/app/domains/erp/payments/data-access/cheque-alert.service'
import { NgTemplateOutlet } from '@angular/common'
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router'
import { revealExpandedAccordion } from '@/app/core/ui/side-menu'

// ─── Ionic & Capacitor ───────────────────────────
import {
    NavController,
    Platform,
    IonIcon,
} from '@ionic/angular/standalone'
import { ModalService } from '@/app/core/ui/modal.service'
import {
    IonTabs,
    IonTabBar,
    IonTabButton,
    IonLabel,
    IonSplitPane,
    IonMenu,
    IonContent,
    IonFooter,
    IonList,
    IonListHeader,
    IonMenuToggle,
    IonRouterOutlet,
    IonItem,
    IonButton,
    IonBadge,
    IonPopover,
    IonAccordionGroup,
    IonAccordion,
} from '@ionic/angular/standalone'
import { addIcons } from 'ionicons'
import { moonOutline, notificationsOutline, sunnyOutline } from 'ionicons/icons'

// ─── RxJS ────────────────────────────────────────
import { Subject } from 'rxjs'
import { takeUntil, filter } from 'rxjs/operators'

// ─── Project (Alias) ─────────────────────────────
import { AddPurchaseModalPage } from '@/app/domains/erp/purchases/modals/add-purchase-modal/add-purchase-modal.page'
import { AddSaleModalPage } from '@/app/domains/erp/sales/modals/add-sale-modal/add-sale-modal.page'
import { AddResourceModalPage } from '@/app/domains/resource/catalog/modals/add-resource-modal/add-resource-modal.page'
import { AddCategoryModalPage } from '@/app/domains/resource/categories/modals/add-category-modal/add-category-modal.page'
import { PlatformService } from '@/app/core/platform/platform.service'
import { UiServiceService } from '@/app/services/ui.service'
import { EmployeeService } from '@/app/domains/auth/employee/data-access/employee.service'
import { ThemeService } from '@/app/core/theme/theme.service'

import { TranslatePipe } from '@ngx-translate/core'
@Component({
    selector: 'app-tabs',
    templateUrl: 'tabs.page.html',
    styleUrls: ['tabs.page.scss'],
    imports: [
        IonIcon,
        IonTabs,
        IonTabBar,
        IonTabButton,
        IonLabel,
        IonSplitPane,
        IonMenu,
        IonContent,
        IonFooter,
        IonList,
        IonListHeader,
        IonMenuToggle,
        IonRouterOutlet,
        IonItem,
        IonButton,
        IonBadge,
        IonPopover,
        IonAccordionGroup,
        IonAccordion,
        RouterLink,
        RouterLinkActive,
        NgTemplateOutlet,
        TranslatePipe,
    ],
})
export class TabsPage implements OnInit, OnDestroy {
    closed$ = new Subject<any>()
    showTabs = true
    // false mientras una lista larga está deslizada hacia abajo (HideChromeOnScrollDirective)
    tabsVisible = true
    sideMenuOpen = true

    // Oculto mientras las notificaciones sean dummy; volver a true al conectar NOTIFICATION.
    readonly showNotifications = false

    // Dummy mientras no exista un backend de notificaciones conectado (no hay
    // NotificationService ni ruta al microservicio NOTIFICATION todavía).
    readonly notifications: { title: string; time: string; read: boolean }[] = [
        { title: 'Stock bajo: Harina 000 en Bodega Central', time: 'Hace 10 min', read: false },
        { title: 'Nueva venta registrada: Boleta #1245', time: 'Hace 1 hora', read: false },
        { title: 'Recordatorio: cobranza vence mañana', time: 'Hace 3 horas', read: true },
    ]

    get unreadNotificationsCount(): number {
        return this.notifications.filter((notification) => !notification.read).length
    }

    // Ítems del menú, compartidos entre el tab-bar (teléfono) y el menú lateral (tablet/desktop)
    appPages = [
        {
            title: 'NAV.SALES',
            url: 'branch-sales',
            icon: 'hugeicons--sale-tag-02',
            route: 'branch-sales',
        },
        {
            title: 'NAV.PURCHASES',
            url: 'branch-purchases',
            icon: 'hugeicons--shopping-cart-check-in-02',
            route: 'branch-purchases',
        },
        {
            title: 'NAV.PLANNING',
            url: 'planning-container',
            icon: 'hugeicons--apple-reminder',
            route: 'planning-container',
        },
        {
            title: 'NAV.MANAGEMENT',
            url: 'menu',
            icon: 'hugeicons--more',
            route: 'menu',
        },
    ]

    // Submenús desplegables de "Menú" — mismas categorías/labels/colores/rutas
    // que el dashboard de menu.page.ts (categories). Si se agregan o renombran
    // módulos ahí, replicar el cambio acá también (ver nota de duplicación).
    menuCategories: {
        value: string
        title: string
        icon: string
        color: string
        items: { title: string; route: string; icon: string; queryParams: Record<string, string> }[]
    }[] = [
        {
            value: 'relations',
            title: 'MENU.CATEGORIES.RELATIONS.LABEL',
            icon: 'hugeicons--chart-relationship',
            color: 'secondary',
            items: [
                { title: 'MENU.MODULES.CUSTOMERS.LABEL', route: 'menu/customers', icon: 'hugeicons--contact-01', queryParams: {} },
                { title: 'MENU.MODULES.PROVIDERS.LABEL', route: 'menu/suppliers', icon: 'hugeicons--permanent-job', queryParams: {} },
                { title: 'MENU.MODULES.EMPLOYEES.LABEL', route: 'menu/team', icon: 'hugeicons--user-multiple-02', queryParams: {} },
            ],
        },
        {
            value: 'inventory',
            title: 'MENU.CATEGORIES.INVENTORY.LABEL',
            icon: 'hugeicons--archive-02',
            color: 'warning',
            items: [
                { title: 'MENU.MODULES.INVENTORY_MANAGEMENT.LABEL', route: 'menu/inventory-management', icon: 'hugeicons--blockchain-01', queryParams: {} },
                { title: 'MENU.MODULES.INVENTORY_TRANSFERS.LABEL', route: 'menu/inventory-container', icon: 'hugeicons--arrow-data-transfer-horizontal', queryParams: {} },
                { title: 'MENU.MODULES.FIXED_ASSETS.LABEL', route: 'menu/main-asset', icon: 'hugeicons--activity-02', queryParams: {} },
                { title: 'MENU.MODULES.SUPPLIES.LABEL', route: 'menu/main-supply', icon: 'hugeicons--atom-02', queryParams: {} },
                { title: 'MENU.MODULES.PRODUCTS.LABEL', route: 'menu/main-products', icon: 'hugeicons--shopping-bag-01', queryParams: {} },
                { title: 'MENU.MODULES.CATEGORIES.LABEL', route: 'menu/categories', icon: 'hugeicons--dashboard-square-02', queryParams: {} },
                { title: 'MENU.MODULES.BUILDINGS.LABEL', route: 'menu/building-container', icon: 'hugeicons--warehouse', queryParams: {} },
            ],
        },
        {
            value: 'finance',
            title: 'MENU.CATEGORIES.FINANCE.LABEL',
            icon: 'hugeicons--money-security',
            color: 'success',
            items: [
                { title: 'MENU.MODULES.CHECKOUT.LABEL', route: 'menu/checkout-main', icon: 'hugeicons--cashier', queryParams: {} },
                { title: 'MENU.MODULES.BANKS.LABEL', route: 'menu/bank-management', icon: 'hugeicons--bank', queryParams: {} },
                { title: 'NAV.ITEMS.PAYMENT_METHODS', route: 'menu/payment-methods-config', icon: 'hugeicons--money-security', queryParams: {} },
                { title: 'MENU.MODULES.RECEIVABLES.LABEL', route: 'menu/accounts', icon: 'hugeicons--dollar-receive-02', queryParams: { type: '1' } },
                { title: 'MENU.MODULES.PAYABLES.LABEL', route: 'menu/accounts', icon: 'hugeicons--dollar-send-02', queryParams: { type: '2' } },
                { title: 'MENU.MODULES.BALANCE_SHEET.LABEL', route: 'menu/balance-sheet', icon: 'hugeicons--audit-01', queryParams: {} },
            ],
        },
        {
            value: 'operations',
            title: 'MENU.CATEGORIES.OPERATIONS.LABEL',
            icon: 'hugeicons--chart-histogram',
            color: 'tertiary',
            items: [
                { title: 'MENU.MODULES.REPORTING.LABEL', route: 'menu/reporting-main', icon: 'hugeicons--chart-histogram', queryParams: {} },
                { title: 'MENU.MODULES.PROMOTIONS.LABEL', route: 'menu/promotions', icon: 'hugeicons--discount-tag-02', queryParams: {} },
                { title: 'MENU.MODULES.TERRITORY.LABEL', route: 'menu/territory', icon: 'hugeicons--map-pin', queryParams: {} },
                { title: 'MENU.MODULES.EVENTS.LABEL', route: 'menu/event-main', icon: 'hugeicons--calendar-love-01', queryParams: {} },
                { title: 'NAV.ITEMS.ALERTS_SETTINGS', route: 'menu/settings', icon: 'hugeicons--calendar-setting-01', queryParams: {} },
            ],
        },
    ]

    // Categoría desplegada del submenú — acordeón de selección única: al
    // navegar a otra categoría, las demás se colapsan solas para facilitar
    // moverse entre submenús (ver ngOnInit y onSubCategoryChange).
    openSubCategory: string | null = null

    // Estado del acordeón "Gestión". El contenido del menú desplegado se
    // destruye al plegarlo (queda la franja de íconos), así que se guarda acá
    // para que vuelva a abrirse tal como el usuario lo dejó.
    gestionAccordionValue: string | null = null

    // Scroll del menú desplegado (no existe con el menú plegado) — ver revealExpandedAccordion.
    @ViewChild('menuContent') private menuContent?: IonContent

    // Lista de rutas donde los tabs deben ocultarse
    urlsToHideTabs = [
        'category-edit',
        'purchase-view',
        'sale-view',
        'resource-edit',
        'company-edit',
        'company-container',
        'natural-person-edit',
        'profile',
        'employee-edit',
        'building-detail',
        'inventory-container',
        'transfer-container',
        'planning-edit',
        'manufacturing-container',
        'promotion-edit',
        'promotion-container',
        'replenishment-modal',
        'cashier',
        'open-cash-register',
        'inventory-management',
        'checkout-main',
        'provider-container',
        'customers',
        'employee-container',
        'building-container',
        'categories',
        'main-supply',
        'accounts',
        'company-summary',
        'cheques',
        'settings',
        'reporting-main',
        'territory',
        'main-products',
        'bank-management',
        'balance-sheet',
        'event-main',
        'event-edit',
        'promotions',
        'suppliers',
    ]

    constructor(
        private modalService: ModalService,
        public platform: Platform,
        private router: Router,
        private navController: NavController,
        private platformService: PlatformService,
        public uiService: UiServiceService,
        private employeeService: EmployeeService,
        public themeService: ThemeService,
        private chequeAlerts: ChequeAlertService
    ) {
        addIcons({ notificationsOutline, moonOutline, sunnyOutline })
    }

    get isPhoneShell(): boolean {
        return this.platformService.isPhoneShell()
    }

    // "Menú" se renderiza aparte en el menú lateral (acordeón con sus
    // submenús anidados) en vez de como ítem plano — ver tabs.page.html.
    get sideMenuTopPages() {
        return this.appPages.filter((page) => page.route !== 'menu')
    }

    /** Globo del menú: cheques vencidos o por vencer en Cuentas por Cobrar (type 1) y por Pagar (type 2). 0 = sin globo. */
    badgeFor(item: { route: string; queryParams?: any }): number {
        if (item.route !== 'menu/accounts') return 0
        return this.chequeAlerts.count(item.queryParams?.type === '2' ? 'payable' : 'receivable')
    }

    ngOnInit() {
        void this.chequeAlerts.refresh(true)
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntil(this.closed$)
            )
            .subscribe((event: NavigationEnd) => {
                void this.chequeAlerts.refresh() // como máximo una vez por minuto
                // Oculta los tabs si la URL contiene alguna de las rutas en urlsToHideTabs
                this.showTabs = !this.urlsToHideTabs.some((fragment) =>
                    event.urlAfterRedirects.includes(fragment)
                )
                // Cada página nueva parte con el tab bar y el encabezado visibles.
                this.uiService.setNavBarsVisible(true)

                // Al navegar, la categoría de la página activa pasa a ser la
                // única abierta — el resto se colapsa sola.
                this.syncOpenCategoryWithRoute()
            })
        this.syncOpenCategoryWithRoute()

        if (!this.isPhoneShell) {
            // Estado inicial del menú lateral: abierto en desktop ancho, colapsado en
            // tablet/desktop angosto. No se re-evalúa en resize para no pisar un toggle manual.
            this.uiService.setSideMenuOpen(window.innerWidth >= 1200)
        }

        this.uiService.sideMenuOpen$.pipe(takeUntil(this.closed$)).subscribe((open) => {
            this.sideMenuOpen = open
        })

        this.uiService.navBarsVisible$.pipe(takeUntil(this.closed$)).subscribe((visible) => {
            this.tabsVisible = visible
        })
    }

    ngOnDestroy() {
        this.closed$.next(true) // <-- close subscription when component is destroyed
    }

    isSelected(page: any): boolean {
        const url = this.router.url
        return url === `/${page.url}` || url.startsWith(`/${page.url}/`)
    }

    // Categoría (Relaciones y contactos / Inventarios.../ Finanzas / Otros)
    // a la que pertenece la página activa, o null si la activa no es un submenú.
    get activeCategoryValue(): string | null {
        const url = this.router.url.split('?')[0]
        const category = this.menuCategories.find((cat) =>
            cat.items.some((item) => url === `/${item.route}` || url.startsWith(`/${item.route}/`))
        )
        return category ? category.value : null
    }

    private syncOpenCategoryWithRoute() {
        const active = this.activeCategoryValue
        if (active) {
            this.openSubCategory = active
        }
    }

    // El usuario puede plegar/desplegar libremente las categorías para
    // explorarlas; el acordeón es de selección única, así que abrir una
    // colapsa la anterior sola.
    onSubCategoryChange(event: Event) {
        const value = (event as CustomEvent<{ value?: string | string[] }>).detail.value
        this.openSubCategory = Array.isArray(value) ? (value[0] ?? null) : (value ?? null)
        if (this.openSubCategory) this.revealExpandedAccordion(event.currentTarget)
    }

    // ionChange burbujea desde el acordeón de categorías anidado: solo cuenta
    // el evento emitido por el propio grupo "Gestión".
    onGestionAccordionChange(event: Event) {
        if (event.target !== event.currentTarget) return
        const value = (event as CustomEvent<{ value?: string | string[] }>).detail.value
        this.gestionAccordionValue = Array.isArray(value) ? (value[0] ?? null) : (value ?? null)
        if (this.gestionAccordionValue) this.revealExpandedAccordion(event.currentTarget)
    }

    // Scroll para mostrar completo el acordeón recién desplegado — compartido con admin-shell (core/ui/side-menu.ts).
    private revealExpandedAccordion(group: EventTarget | null) {
        revealExpandedAccordion(group, this.menuContent)
    }

    home() {
        this.navController.navigateRoot('/')
    }

    async logoutFromMenu() {
        const confirmed = await this.uiService.confirmLogout()
        if (confirmed) {
            await this.employeeService.logout()
        }
    }

    async presentModalCategory() {
        const modal = await this.modalService.create(AddCategoryModalPage, {
            preset: 'page',
            backdropDismiss: false,
        })
        await modal.present()
        await modal.onWillDismiss()
    }

    async presentModalPurchase() {
        const modal = await this.uiService.presentUniqueFullModal('AddPurchaseModalPage', AddPurchaseModalPage)
        await modal?.onWillDismiss()
    }

    async presentModalSale() {
        await this.uiService.runExclusive('add-sale', async () => {
            const modal = await this.modalService.create(AddSaleModalPage, {
                preset: 'page',
                backdropDismiss: false,
            })
            await modal.present()
            await modal.onWillDismiss()
        })
    }

    async presentModalResource() {
        const modal = await this.modalService.create(AddResourceModalPage, {
            preset: 'page',
            backdropDismiss: false,
        })
        await modal.present()
        await modal.onWillDismiss()
    }
}
